import { BaseLogger } from './base-logger';
import { BaseLogData, LoggerConfig } from './types';

enum LogLevel {
  LOG = 'log',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

type LogData = BaseLogData & {
  level: LogLevel;
  message: string;
  url: string;
  stacktrace?: string;
};

type ConsoleMethod = (message?: unknown, ...optionalParams: unknown[]) => void;
type LoggerConsoleMethods = Record<LogLevel, ConsoleMethod>;

export class ConsoleLogger extends BaseLogger<LogData> {
  private originalConsole: LoggerConsoleMethods;
  private unloadHandler: () => void = () => this.cleanup();

  private persistentLogBuffer: LogData[] = [];
  private static readonly MAX_LOGS = process.env.NODE_ENV === 'production' ? 200 : 2000;

  constructor(config: LoggerConfig) {
    super(config);
    this.originalConsole = this.storeOriginalConsoleMethods();
    this.setupConsoleInterceptors();
    this.setupUnloadHandler();
  }

  private storeOriginalConsoleMethods(): LoggerConsoleMethods {
    return Object.values(LogLevel).reduce(
      (acc, type) => ({
        ...acc,
        [type]: console[type],
      }),
      {} as LoggerConsoleMethods
    );
  }
  private setupConsoleInterceptors(): void {
    Object.values(LogLevel).forEach((level) => {
      (console[level] as ConsoleMethod) = (message?: unknown, ...args: unknown[]): void => {
        // Call original method
        this.originalConsole[level](message, ...args);

        // Serialize all arguments into one string
        const fullMessage = [message, ...args]
          .map((arg) => {
            if (typeof arg === 'string') return arg;

            if (arg instanceof Error) {
              return `${arg.name}: ${arg.message}`;
            }

            try {
              return JSON.stringify(arg);
            } catch {
              return '[Unserializable]';
            }
          })
          .join(' ');

        // Generate log entry
        const entry: LogData = {
          level,
          message: fullMessage || 'Empty message',
          url: window.location.href,
          timestamp: new Date().getTime(),
          stacktrace: (message instanceof Error && message.stack) || new Error().stack?.split('\n').slice(2).join('\n'), // Skip first 2 lines (Error and collectLogData)
          ...this.getCommonData(),
        };
        this.addToQueue(entry);

        // Add to persistent log buffer (for feedback form)
        if (process.env.NODE_ENV !== 'production' || level !== LogLevel.LOG) {
          this.persistentLogBuffer.push(entry);
          if (this.persistentLogBuffer.length > ConsoleLogger.MAX_LOGS) {
            this.persistentLogBuffer.shift();
          }
        }
      };
    });
  }

  private setupUnloadHandler(): void {
    window.addEventListener('beforeunload', this.unloadHandler);
    window.addEventListener('unload', this.unloadHandler);
  }

  public override cleanup(): void {
    super.cleanup();
    window.removeEventListener('beforeunload', this.unloadHandler);
    window.removeEventListener('unload', this.unloadHandler);

    Object.values(LogLevel).forEach((type) => {
      (console[type] as ConsoleMethod) = this.originalConsole[type];
    });
  }

  public getLogBlob(): Blob {
    return new Blob([JSON.stringify(this.persistentLogBuffer, null, 2)], {
      type: 'application/json',
    });
  }
}
